import { useState } from 'react';
import { ItemPhotoSection } from '@/components/ItemPhotoSection';
import { PhotoLightbox } from '@/components/PhotoLightbox';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Pencil, Trash2 } from 'lucide-react';
import { items as itemApi } from '@/api/endpoints';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tag } from '@/components/ui/tag';

export function ItemDetailPage() {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [zoomed, setZoomed] = useState<string | null>(null);
  const navigate = useNavigate();
  const qc = useQueryClient();

  const item = useQuery({ queryKey: ['items', id], queryFn: () => itemApi.get(Number(id)), enabled: !!id });

  const upload = useMutation({
    mutationFn: (file: File) => itemApi.uploadPhoto(Number(id), file, 'ITEM'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['items', id] }),
  });

  const uploadReceipt = useMutation({
    mutationFn: (file: File) => itemApi.uploadPhoto(Number(id), file, 'RECEIPT'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['items', id] }),
  });

  const remove = useMutation({
    mutationFn: () => itemApi.remove(Number(id)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['items'] });
      navigate('/items');
    },
  });

  const removePhoto = useMutation({
    mutationFn: (photoId: number) => itemApi.removePhoto(Number(id), photoId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['items', id] }),
  });

  if (item.isLoading) {
    return <div className="text-sm text-muted-foreground">{t('common.loading')}</div>;
  }
  if (!item.data) return null;
  const it = item.data;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-semibold">{it.name}</h1>
          <p className="text-sm text-muted-foreground">{it.locationName}</p>
        </div>
        <div className="flex gap-2">
          <Button asChild variant="secondary"><Link to={`/items/${it.id}/edit`}><Pencil className="mr-2 h-4 w-4" />{t('common.edit')}</Link></Button>
          <Button variant="danger" onClick={() => { if (window.confirm(t('items.deleteConfirm'))) remove.mutate(); }}>
            <Trash2 className="mr-2 h-4 w-4" />{t('common.delete')}
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div className="space-y-4 md:col-span-2">
          <ItemPhotoSection
            title={t('items.photos')}
            photos={it.photos.filter((p) => p.photoType === 'ITEM')}
            addLabel={t('items.addPhoto')}
            openLabel={t('items.openPhoto')}
            removeLabel={t('items.removePhoto')}
            onUpload={(f) => upload.mutate(f)}
            onRemove={(photoId) => removePhoto.mutate(photoId)}
            onZoom={setZoomed}
          />
          <ItemPhotoSection
            title={t('items.receipts')}
            photos={it.photos.filter((p) => p.photoType === 'RECEIPT')}
            addLabel={t('items.addReceipt')}
            openLabel={t('items.openReceipt')}
            removeLabel={t('items.removeReceipt')}
            onUpload={(f) => uploadReceipt.mutate(f)}
            onRemove={(photoId) => removePhoto.mutate(photoId)}
            onZoom={setZoomed}
          />
        </div>
        <Card>
          <CardHeader><CardTitle>{t('items.title')}</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            <Field label={t('items.category')} value={i18n.language?.startsWith('tr') ? it.categoryNameTr : it.categoryNameEn} />
            <Field label={t('items.quantity')} value={String(it.quantity)} />
            <Field label={t('items.brand')} value={it.brand} />
            <Field label={t('items.model')} value={it.model} />
            <Field label={t('items.purchaseDate')} value={it.purchaseDate} />
            <Field label={t('items.purchasePrice')} value={it.purchasePrice ? `${it.purchasePrice} ${it.currency ?? ''}` : null} />
            <Field label={t('items.currentValue')} value={it.currentValue ? `${it.currentValue} ${it.currency ?? ''}` : null} />
            <Field label={t('items.warrantyUntil')} value={it.warrantyUntil} />
            <Field label={t('items.condition')} value={it.condition ? t(`items.conditions.${it.condition}`) : null} />
            <Field label={t('items.uploadedBy')} value={it.createdByEmail} />
            <div className="pt-2">
              <div className="text-xs text-muted-foreground">{t('items.tags')}</div>
              <div className="flex flex-wrap gap-1 pt-1">
                {it.tags.map((tg) => (<Tag key={tg.id} tone="neutral">{tg.name}</Tag>))}
                {it.tags.length === 0 && <span className="text-xs text-muted-foreground">—</span>}
              </div>
            </div>
            {it.description && <p className="pt-2 text-muted-foreground">{it.description}</p>}
          </CardContent>
        </Card>
      </div>
      {zoomed && <PhotoLightbox src={zoomed} onClose={() => setZoomed(null)} />}
    </div>
  );
}

function Field({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right">{value || '—'}</span>
    </div>
  );
}
